import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Building2,
  Users,
  Handshake,
  Brain,
  ShieldCheck,
  BadgeCheck,
  Layers,
  Target,
  Globe2,
} from "lucide-react";
import {
  IMAGES,
  HOME_SOLUTIONS,
  RECRUITMENT_AREAS,
  TRUST_VALUES,
  CANDIDATE_JOURNEY,
  PARTNERSHIPS,
  RESOURCES,
} from "../data/site";
import Reveal from "../components/common/Reveal";
import SectionHeading from "../components/common/SectionHeading";
import JourneyStepper from "../components/common/JourneyStepper";
import CTABand from "../components/common/CTABand";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";

const HERO_DEFAULT = {
  eyebrow: "Specialist Healthcare Recruitment • Precision & Judgement",
  title_lead: "Exceptional healthcare talent.",
  title_highlight: "Precisely matched.",
  subtitle: "We connect healthcare employers with exceptional clinical and non-clinical professionals through rigorous screening, intelligent matching and trusted recruitment expertise.",
  cta_primary_label: "Request Talent",
  cta_secondary_label: "Explore Opportunities",
  image_url: IMAGES.hero,
  badges: ["Specialist healthcare expertise", "Intelligent candidate matching", "Compliance-focused recruitment"],
  card_title: "Precision over volume",
  card_text: "Every professional is intelligently matched, compliance-checked and consultant-reviewed before being presented to you.",
};

const Hero = ({ c }) => (
  <section className="relative overflow-hidden bg-navy pt-32 lg:pt-40" data-testid="hero-section">
    <div className="absolute -right-24 top-24 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-16 pt-10 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pb-24">
      <Reveal>
        <span className="overline inline-block rounded-full border border-white/15 px-4 py-1.5 text-xs text-gold-light">
          {c.eyebrow}
        </span>
        <h1 className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {c.title_lead}{" "}
          <span className="text-gold">{c.title_highlight}</span>
        </h1>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/75">
          {c.subtitle}
        </p>
        <div className="mt-9 flex flex-col gap-4 sm:flex-row">
          <Link to="/employers#request" className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-gold-dark" data-testid="hero-request-talent">
            {c.cta_primary_label} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/candidates#submit" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="hero-submit-cv">
            {c.cta_secondary_label}
          </Link>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70">
          {(c.badges || []).map((t) => (
            <span key={t} className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-gold" /> {t}
            </span>
          ))}
        </div>
      </Reveal>

      <Reveal delay={120} className="relative">
        <div className="overflow-hidden rounded-2xl ring-1 ring-white/15 shadow-2xl">
          <img src={c.image_url} alt="Two healthcare professionals in a clinical setting" className="h-[420px] w-full object-cover lg:h-[520px]" />
        </div>
        <div className="absolute -bottom-6 -left-6 hidden max-w-[240px] rounded-xl border border-border bg-white p-5 shadow-xl sm:block">
          <p className="font-serif text-lg text-navy">{c.card_title}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {c.card_text}
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const TrustStrip = () => (
  <section className="border-b border-border bg-white py-10" data-testid="trust-section">
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-12 gap-y-6 px-6 lg:justify-between lg:px-8">
      {TRUST_VALUES.map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center gap-3 text-navy">
          <Icon className="h-5 w-5 text-gold" />
          <span className="text-sm font-semibold tracking-tight">{label}</span>
        </div>
      ))}
    </div>
  </section>
);

const ServicesOverview = () => (
  <section className="py-20 lg:py-24" data-testid="services-overview">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <SectionHeading overline="Our Solutions" title="Recruitment shaped around your needs." description="From specialist sourcing to compliant placement, we provide focused recruitment support designed around the workforce you need today and the capability you will need tomorrow." />
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {HOME_SOLUTIONS.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal key={s.key} delay={(i % 3) * 80}>
              <Link to={s.to} className="group flex h-full flex-col rounded-2xl border border-border bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-md" data-testid={`service-card-${s.key}`}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors group-hover:bg-gold group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-serif text-xl text-navy">{s.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                  Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const HOME_WHY = [
  { icon: Target, title: "Specialist Focus", description: "Healthcare is our core recruitment focus." },
  { icon: BadgeCheck, title: "Quality Before Volume", description: "We prioritise relevance, suitability and long-term fit." },
  { icon: Brain, title: "Human Judgement", description: "Technology supports our process. Professional judgement remains essential." },
  { icon: ShieldCheck, title: "Compliance Mindset", description: "We approach recruitment with the regulatory and documentation requirements of healthcare in mind." },
  { icon: Handshake, title: "Partnership Approach", description: "We build relationships designed to create value beyond a single placement." },
  { icon: Globe2, title: "International Reach", description: "We support cross-border recruitment across healthcare markets, connecting international professionals with the right opportunities and partners." },
];

const WhyChoose = () => (
  <section className="bg-white py-20 lg:py-24" data-testid="why-choose-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <SectionHeading overline="Why Azimi & Co." title="Precision, expertise and trust — working together." description="Healthcare recruitment demands more than access to candidates. It requires sound judgement, disciplined screening and a clear understanding of what makes the right fit." />
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {HOME_WHY.map((w, i) => {
          const Icon = w.icon;
          return (
            <Reveal key={w.title} delay={(i % 3) * 80}>
              <div className="flex h-full gap-4 rounded-2xl border border-border bg-background p-6">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold/10 text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <h4 className="font-serif text-lg text-navy">{w.title}</h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.description}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const AudienceBlock = ({ reverse, image, imagePosition = "object-center", overline, title, description, points, cta, secondary, testid }) => (
  <section className="py-20 lg:py-24" data-testid={testid}>
    <div className={`mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 ${reverse ? "" : ""}`}>
      <Reveal className={reverse ? "lg:order-2" : ""}>
        <div className="overflow-hidden rounded-2xl border border-border shadow-sm">
          <img src={image} alt={title} className={`h-[380px] w-full object-cover ${imagePosition}`} />
        </div>
      </Reveal>
      <Reveal delay={100} className={reverse ? "lg:order-1" : ""}>
        <span className="overline text-xs text-gold">{overline}</span>
        <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">{title}</h2>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground">{description}</p>
        <ul className="mt-6 space-y-3">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm text-charcoal">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> {p}
            </li>
          ))}
        </ul>
        <Link to={cta.to} className="mt-8 inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-light" data-testid={`${testid}-cta`}>
          {cta.label} <ArrowRight className="h-4 w-4" />
        </Link>
        {secondary && (
          <Link to={secondary.to} className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors hover:text-gold" data-testid={`${testid}-secondary-cta`}>
            {secondary.label} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </Reveal>
    </div>
  </section>
);

const HOME_PROCESS_STEPS = [
  { title: "Understand", description: "We begin with your requirements, workforce context and hiring priorities." },
  { title: "Source", description: "We identify relevant professionals through focused and specialist sourcing." },
  { title: "Assess", description: "We evaluate experience, qualifications, suitability and relevant compliance requirements." },
  { title: "Match", description: "We combine recruitment expertise, structured assessment and intelligent matching to identify the strongest fit." },
  { title: "Deliver", description: "We coordinate the next stage of the process with clarity, responsiveness and professional care." },
  { title: "Support", description: "We remain focused on a smooth candidate and employer experience through the placement journey." },
];

const ProcessSection = () => (
  <section className="bg-white py-20 lg:py-24" data-testid="process-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <SectionHeading overline="How We Work" title="Discipline at every stage." description="A disciplined recruitment process designed to improve quality, reduce friction and create stronger matches." />
      <div className="mt-16">
        <JourneyStepper steps={HOME_PROCESS_STEPS} accent="gold" />
      </div>
    </div>
  </section>
);

const PARTNERSHIP_VALUE_POINTS = [
  "Expanded talent access",
  "Specialist healthcare sourcing",
  "Structured candidate delivery",
  "Transparent collaboration",
  "Flexible partnership models",
];

const PartnershipSection = () => (
  <section className="py-20 lg:py-24" data-testid="partnership-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <SectionHeading overline="Recruitment Partnerships" title="Stronger recruitment through strategic partnership." description="We work with recruitment agencies, talent partners and specialist providers to extend sourcing capability, strengthen delivery and create more opportunities for the right healthcare professionals." />
      <Reveal>
        <ul className="mx-auto mt-8 flex max-w-4xl flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="partnership-value-points">
          {PARTNERSHIP_VALUE_POINTS.map((p) => (
            <li key={p} className="flex items-center gap-2 text-sm font-medium text-charcoal">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-gold" /> {p}
            </li>
          ))}
        </ul>
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PARTNERSHIPS.map((p, i) => {
          const Icon = p.icon;
          return (
            <Reveal key={p.slug} delay={(i % 3) * 80}>
              <Link to={`/partnerships/${p.slug}`} className="group flex h-full flex-col rounded-2xl border border-border bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-md" data-testid={`partnership-card-${p.slug}`}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10 text-gold">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-serif text-lg text-navy">{p.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">{p.summary}</p>
              </Link>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-10 text-center">
        <Link to="/partnerships" className="inline-flex items-center gap-2 rounded-lg bg-gold px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-gold-dark" data-testid="home-become-partner">
          Explore Partnerships <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ResourcesTeaser = () => (
  <section className="bg-white py-20 lg:py-24" data-testid="resources-teaser">
    <div className="mx-auto max-w-7xl px-6 lg:px-8">
      <SectionHeading overline="Resources" title="Insight for employers, candidates and partners" description="Practical guidance on working with a specialist recruitment partner — from briefing a role to preparing for interview." />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {RESOURCES.map((r, i) => {
          const Icon = r.icon;
          return (
            <Reveal key={r.title} delay={(i % 4) * 70}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-background p-6">
                <Icon className="h-6 w-6 text-gold" />
                <h4 className="mt-4 font-serif text-lg text-navy">{r.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.description}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-10 text-center">
        <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold">
          Explore all resources <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const INTELLIGENCE_PILLARS = [
  { icon: ShieldCheck, title: "Clinical-Grade Rigour", body: "Screening and compliance standards designed for regulated healthcare, applied to every search." },
  { icon: Brain, title: "Intelligent Matching", body: "Technology identifies the strongest candidates. Our consultants make the final decision." },
  { icon: BadgeCheck, title: "Consultant Review", body: "Every profile is reviewed and approved by an experienced consultant before it reaches you." },
  { icon: Layers, title: "Built to Scale", body: "A structured approach that grows with your workforce — across services and borders." },
];

const IntelligenceSection = () => (
  <section className="relative overflow-hidden bg-navy py-20 lg:py-24" data-testid="intelligence-section">
    <div className="absolute -right-24 top-10 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
      <Reveal>
        <span className="overline text-xs text-gold-light">Who We Are</span>
        <h2 className="mt-4 font-serif text-3xl tracking-tight text-white sm:text-4xl">
          Built around people. <span className="text-gold">Driven by precision.</span>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-white/75">
          Azimi &amp; Co. Vision Recruitment is a specialist healthcare recruitment consultancy
          built on expertise, rigour and long-term professional relationships.
        </p>
        <p className="mt-4 text-base leading-relaxed text-white/75">
          Our approach combines specialist market knowledge, rigorous screening, intelligent
          matching and human judgement to help organisations build capable, compliant and
          future-ready teams.
        </p>
        <div className="mt-8 flex flex-col gap-4 sm:flex-row">
          <Link to="/why-choose-us" className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-gold-dark" data-testid="intelligence-discover-expertise">
            Discover Our Expertise <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/about" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="intelligence-learn-more">
            About Azimi &amp; Co.
          </Link>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="grid gap-4 sm:grid-cols-2">
          {INTELLIGENCE_PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid={`intelligence-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-serif text-lg text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{p.body}</p>
              </div>
            );
          })}
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useSeo({
    title: "Specialist Healthcare Recruitment Partner",
    description:
      "Azimi & Co. Vision Recruitment is a specialist healthcare recruitment partner. We combine rigorous screening, intelligent matching and experienced human judgement to connect hospitals and care providers with exceptional clinical and non-clinical professionals — locally and internationally.",
    keywords:
      "Healthcare Recruitment, Specialist Healthcare Recruitment, Healthcare Talent Solutions, Recruitment Partnerships, Nurse Recruitment, International Healthcare Recruitment",
  });
  const [hero, setHero] = useState(HERO_DEFAULT);
  useEffect(() => {
    api.get("/site-content").then(({ data }) => { if (data?.hero) setHero({ ...HERO_DEFAULT, ...data.hero }); }).catch(() => {});
  }, []);
  return (
    <>
      <Hero c={hero} />
      <TrustStrip />
      <ServicesOverview />
      {/* Recruitment areas */}
      <section className="bg-white py-20 lg:py-24" data-testid="areas-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading overline="Areas We Recruit" title="Specialist expertise across healthcare disciplines" description="A deep understanding of the roles that keep clinical services running — and the standards each one requires." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {RECRUITMENT_AREAS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={(i % 4) * 70}>
                  <div className="rounded-2xl border border-border bg-background p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                    <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h4 className="mt-5 font-serif text-lg text-navy">{a.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <WhyChoose />
      <IntelligenceSection />
      <AudienceBlock
        testid="employer-section"
        image={IMAGES.employers}
        overline="For Employers"
        title="The right talent. Without the noise."
        description="We help healthcare organisations identify, assess and secure the professionals they need through a structured recruitment process built around quality, compliance and fit."
        points={["Specialist sourcing", "Rigorous screening", "Compliance-focused process", "Intelligent matching", "Responsive partnership", "Long-term workforce value"]}
        cta={{ label: "Request Talent", to: "/employers#request" }}
      />
      <AudienceBlock
        reverse
        testid="candidate-section"
        image={IMAGES.candidate}
        imagePosition="object-top"
        overline="For Candidates"
        title="Your next opportunity should move your career forward."
        description="We connect healthcare professionals with relevant opportunities through a recruitment experience built around clarity, respect and long-term career value."
        points={["Relevant opportunities", "Professional guidance", "Transparent communication", "Compliance support", "International career pathways"]}
        cta={{ label: "Explore Opportunities", to: "/candidates" }}
        secondary={{ label: "Submit Your CV", to: "/candidates#submit" }}
      />
      <ProcessSection />
      <PartnershipSection />
      <ResourcesTeaser />
      <CTABand
        eyebrow="Let’s Build the Right Team"
        title="The right people make the difference."
        description="Whether you are hiring, exploring your next opportunity or looking to strengthen your recruitment capability, our team is ready to help."
        primary={{ label: "Request Talent", to: "/employers#request" }}
        secondary={{ label: "Submit Your CV", to: "/candidates#submit" }}
      />
    </>
  );
}
